import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, GripVerticalIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FormField } from './FormField';
export interface CaptureField {
  id: string;
  type: 'preset' | 'custom';
  preset?: 'name' | 'email' | 'phone' | 'address' | 'company';
  label?: string;
  description?: string;
  required: boolean;
}
interface CaptureFieldsEditorProps {
  fields: CaptureField[];
  onChange: (fields: CaptureField[]) => void;
}
export function CaptureFieldsEditor({
  fields,
  onChange
}: CaptureFieldsEditorProps) {
  const {
    t
  } = useTranslation('common');
  const [newFieldType, setNewFieldType] = useState<'preset' | 'custom'>('preset');
  const [newFieldPreset, setNewFieldPreset] = useState<'name' | 'email' | 'phone' | 'address' | 'company'>('name');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const handleAddField = () => {
    if (newFieldType === 'preset') {
      // Check if this preset already exists
      const presetExists = fields.some(field => field.type === 'preset' && field.preset === newFieldPreset);
      if (presetExists) {
        alert(`A field for "${getPresetLabel(newFieldPreset)}" already exists.`);
        return;
      }
      const newField: CaptureField = {
        id: `preset-${newFieldPreset}-${Date.now()}`,
        type: 'preset',
        preset: newFieldPreset,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
    } else if (newFieldType === 'custom') {
      if (!newFieldLabel.trim()) {
        alert('Please enter a label for the custom field.');
        return;
      }
      const newField: CaptureField = {
        id: `custom-${Date.now()}`,
        type: 'custom',
        label: newFieldLabel,
        description: newFieldDescription,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
      setNewFieldLabel('');
      setNewFieldDescription('');
    }
    setShowAddForm(false);
  };
  const handleRemoveField = (fieldId: string) => {
    onChange(fields.filter(field => field.id !== fieldId));
  };
  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === fields.length - 1) {
      return;
    }
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newFields[index];
    newFields[index] = newFields[targetIndex];
    newFields[targetIndex] = temp;
    onChange(newFields);
  };
  const handleToggleRequired = (fieldId: string) => {
    onChange(fields.map(field => field.id === fieldId ? {
      ...field,
      required: !field.required
    } : field));
  };
  const getPresetLabel = (preset: 'name' | 'email' | 'phone' | 'address' | 'company'): string => {
    const labels = {
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      address: 'Billing Address',
      company: 'Company Name'
    };
    return labels[preset];
  };
  return <div className="space-y-4" data-id="element-13603">
      <div className="flex justify-between items-center mb-2" data-id="element-13604">
        <h3 className="text-sm font-medium text-gray-700" data-id="element-13605">
          {t('ui.CaptureFieldsEditor.customer_information_fields')}
        </h3>
        <button type="button" onClick={() => setShowAddForm(!showAddForm)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-13606">
          {showAddForm ? 'Cancel' : <>
              <PlusIcon size={14} className="mr-1" data-id="element-13607" />
              {t('ui.CaptureFieldsEditor.add_field')}
            </>}
        </button>
      </div>
      {/* Add new field form */}
      {showAddForm && <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4" data-id="element-13608">
          <div className="mb-3" data-id="element-13609">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-13610">
              {t('ui.CaptureFieldsEditor.field_type')}
            </label>
            <div className="flex space-x-3" data-id="element-13611">
              <label className="flex items-center" data-id="element-13612">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'preset'} onChange={() => setNewFieldType('preset')} data-id="element-13613" />

                <span className="ml-2 text-sm text-gray-700" data-id="element-13614">
                  {t('ui.CaptureFieldsEditor.preset_field')}
                </span>
              </label>
              <label className="flex items-center" data-id="element-13615">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'custom'} onChange={() => setNewFieldType('custom')} data-id="element-13616" />

                <span className="ml-2 text-sm text-gray-700" data-id="element-13617">
                  {t('ui.CaptureFieldsEditor.custom_field')}
                </span>
              </label>
            </div>
          </div>
          {newFieldType === 'preset' ? <div className="mb-3" data-id="element-13618">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-13619">
                {t('ui.CaptureFieldsEditor.preset_field')}
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={newFieldPreset} onChange={e => setNewFieldPreset(e.target.value as any)} data-id="element-13620">
                <option value="name" data-id="element-13621">
                  {t('ui.CaptureFieldsEditor.full_name')}
                </option>
                <option value="email" data-id="element-13622">
                  {t('ui.CaptureFieldsEditor.email_address')}
                </option>
                <option value="phone" data-id="element-13623">
                  {t('ui.CaptureFieldsEditor.phone_number')}
                </option>
                <option value="address" data-id="element-13624">
                  {t('ui.CaptureFieldsEditor.billing_address')}
                </option>
                <option value="company" data-id="element-13625">
                  {t('ui.CaptureFieldsEditor.company_name')}
                </option>
              </select>
            </div> : <>
              <div className="mb-3" data-id="element-13626">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-13627">
                  {t('ui.CaptureFieldsEditor.field_label')}
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('ui.CaptureFieldsEditor.attrs.eg_project_name')} value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} data-id="element-13628" />
              </div>
              <div className="mb-3" data-id="element-13629">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-13630">
                  {t('ui.CaptureFieldsEditor.field_description_optional')}
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('ui.CaptureFieldsEditor.attrs.eg_please_provide_the_project_name')} value={newFieldDescription} onChange={e => setNewFieldDescription(e.target.value)} data-id="element-13631" />
              </div>
            </>}
          <div className="mb-3" data-id="element-13632">
            <label className="flex items-center" data-id="element-13633">
              <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={newFieldRequired} onChange={e => setNewFieldRequired(e.target.checked)} data-id="element-13634" />

              <span className="ml-2 text-sm text-gray-700" data-id="element-13635">
                {t('ui.CaptureFieldsEditor.required_field')}
              </span>
            </label>
          </div>
          <div className="flex justify-end" data-id="element-13636">
            <button type="button" onClick={handleAddField} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm" data-id="element-13637">
              {t('ui.CaptureFieldsEditor.add_field')}
            </button>
          </div>
        </div>}
      {/* List of fields */}
      {fields.length > 0 ? <div className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-13638">
          {fields.map((field, index) => <div key={field.id} className="p-3 flex items-center" data-id="element-13639">
              <div className="flex-shrink-0 mr-2 text-gray-400" data-id="element-13640">
                <GripVerticalIcon size={16} data-id="element-13641" />
              </div>
              <div className="flex-1" data-id="element-13642">
                <div className="flex justify-between items-center" data-id="element-13643">
                  <div data-id="element-13644">
                    <span className="font-medium text-gray-800" data-id="element-13645">
                      {field.type === 'preset' ? getPresetLabel(field.preset!) : field.label}
                    </span>
                    {field.required && <span className="ml-1 text-red-500 text-xs" data-id="element-13646">*</span>}
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-13647">
                    <button type="button" onClick={() => handleToggleRequired(field.id)} className={`text-xs px-2 py-1 rounded ${field.required ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`} data-id="element-13648">
                      {field.required ? 'Required' : 'Optional'}
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'up')} disabled={index === 0} className={`text-gray-400 hover:text-gray-600 ${index === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-13649">
                      <ChevronUpIcon size={16} data-id="element-13650" />
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'down')} disabled={index === fields.length - 1} className={`text-gray-400 hover:text-gray-600 ${index === fields.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-13651">
                      <ChevronDownIcon size={16} data-id="element-13652" />
                    </button>
                    <button type="button" onClick={() => handleRemoveField(field.id)} aria-label="Remove field" className="text-gray-400 hover:text-red-500" data-id="element-13653">
                      <XIcon size={16} aria-hidden="true" data-id="element-13654" />
                    </button>
                  </div>
                </div>
                {field.type === 'custom' && field.description && <p className="text-xs text-gray-500 mt-1" data-id="element-13655">
                    {field.description}
                  </p>}
                <p className="text-xs text-gray-500 mt-1" data-id="element-13656">
                  {field.type === 'preset' ? 'Standard input field' : 'Custom input field'}
                </p>
              </div>
            </div>)}
        </div> : <div className="text-center py-6 bg-gray-50 rounded-md border border-gray-200" data-id="element-13657">
          <p className="text-gray-500" data-id="element-13658">
            {t('ui.CaptureFieldsEditor.no_capture_fields_added')}
          </p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-13659">
            {t('ui.CaptureFieldsEditor.add_fields_to_collect_information_from')}
          </p>
        </div>}
      <div className="text-xs text-gray-500 mt-2" data-id="element-13660">
        {t('ui.CaptureFieldsEditor.these_fields_will_be_shown_on')}
      </div>
    </div>;
}